import { FC, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { parseISO } from 'date-fns';

import { Box, Loader, Modal } from '@qwealth/qcore';

import { formatDate, LONG_MONTH_DATE_FORMAT } from 'data/helpers/dateTimeHelper';
import ClientNotesHistoryModalHeader from './ClientNotesHistoryModalHeader';
import ClientNotesHistoryNoteCard from './ClientNotesHistoryNoteCard';
import { ComplianceApprovalNote, ComplianceChecklistStatus, ComplianceNote, ContactChecklist } from 'data/models/Compliance';
import { getClientNotesHistory } from 'data/actions/compliance';
import ClientNotesHistoryComplianceApprovedCard from './ClientNotesHistoryComplianceApprovedCard';
import { useAppDispatch } from 'data/store';
import { getDateField, sortNotesHistoryByDate } from 'utils/ComplianceUtil';

interface Props {
  contactQID: string | undefined;
  contactName: string | undefined;
  checklistStatus: ComplianceChecklistStatus | undefined;
  isComplianceApproved: boolean | undefined;
  show: boolean;
  onClose: () => void;
}

const ClientNotesHistoryModal: FC<Props> = ({ show, onClose, isComplianceApproved, contactQID, contactName, checklistStatus }): JSX.Element => {
  const dispatch = useAppDispatch();
  const [notes, setNotes] = useState<(ComplianceNote | ComplianceApprovalNote)[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  let noteDateToDisplay = '';

  useEffect(() => {
    setIsLoading(true);
    if (!contactQID) return;

    (async () => {
      const notesFromApi = await dispatch(getClientNotesHistory(contactQID));

      if (!notesFromApi) {
        setIsLoading(false);
        return;
      }

      const sortAndRemoveEmptyDateNotes = sortNotesHistoryByDate(notesFromApi).filter(note => {
        if ('complianceApprovalDate' in note) {
          return !!note.complianceApprovalDate;
        }
        if ('content' in note) {
          return !!note.content;
        }
        return true;
      });

      setNotes(sortAndRemoveEmptyDateNotes);
      setIsLoading(false);
    })();
  }, [contactQID, dispatch]);

  return (
    <Modal
      title={'Compliance Note History'}
      onClose={onClose}
      show={show}
      size="lg"
      titleProps={{
        textStyle: 'sectionTitle',
      }}
    >
      <Modal.Body>
        <Box
          display="flex"
          flexDirection="column"
          maxHeight="80vh"
          backgroundColor="white"
          gap="large"
          overflow="auto"
          paddingRight="large"
          paddingBottom="large"
          minHeight="500px"
        >
          <ClientNotesHistoryModalHeader
            clickBack={onClose}
            contactName={contactName}
            checklistStatus={checklistStatus}
            isComplianceApproved={isComplianceApproved}
          />
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center">
              <Loader />
            </Box>
          ) : (
            <>
              <Box display="flex" flexDirection="column" gap="default">
                {notes.map((note, index) => {
                  const noteDate = getDateField(note);
                  const formattedNoteDate = noteDate ? formatDate(noteDate, LONG_MONTH_DATE_FORMAT) : '';
                  const shouldShowDate = noteDateToDisplay !== formattedNoteDate;
                  let noteKey = '';

                  if ('uuid' in note) {
                    noteKey = `${note.uuid}${note.contentUpdateTime}`;
                  } else if ('complianceApprovalDate' in note) {
                    noteKey = `${note.complianceApprovalDate}`;
                  }

                  if (shouldShowDate) {
                    noteDateToDisplay = formattedNoteDate;
                  }

                  return (
                    <Box key={noteKey}>
                      {shouldShowDate && (
                        <Box fontWeight="bold" marginY="large">
                          {formattedNoteDate}
                        </Box>
                      )}
                      <Box marginLeft="largest">
                        {'contentUpdateTime' in note && <ClientNotesHistoryNoteCard note={note} />}
                        {'complianceApprovalDate' in note && <ClientNotesHistoryComplianceApprovedCard note={note} />}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              {isEmpty(notes) && (
                <Box display="flex" justifyContent="center" alignItems="center">
                  No notes to display.
                </Box>
              )}
            </>
          )}
        </Box>
      </Modal.Body>
    </Modal>
  );
};

export default ClientNotesHistoryModal;
